/**
 * SlideKit — Reusable slide components for the OS2 design system.
 *
 * These components implement the BRIX-inspired aesthetic:
 * clean, airy, blue-dominant with generous whitespace.
 *
 * Usage:
 *   import { SectionTitleSlide, SlideWrapper, SectionLabel, FeatureCard } from '../styleguide/SlideKit';
 */

import { motion } from 'framer-motion';
import { tw, animations } from './tokens';

// ─── Section Label ───────────────────────────────────────────────
// Small blue uppercase text that appears above section headings.
// Like "Problem", "Our Pricing", "Product Overview / Demo" in BRIX.
export const SectionLabel = ({ children, variant = 'light', className = '' }) => {
  const base =
    variant === 'dark'
      ? tw.sectionLabelOnDark
      : variant === 'blue'
      ? tw.sectionLabelOnBlue
      : tw.sectionLabel;
  return <p className={`${base} ${className}`}>{children}</p>;
};

// ─── Section Title Slide ─────────────────────────────────────────
// Full-slide section divider. Two variants: 'light' and 'blue'.
//
// Light: white/gray background, dark text, blue label
// Blue: blue gradient background, white text
export const SectionTitleSlide = ({
  label,
  title,
  titleAccent,
  subtitle,
  variant = 'light',
  icon,
}) => {
  if (variant === 'blue') {
    return (
      <div className="h-full w-full bg-gradient-to-br from-blue-600 via-blue-500 to-blue-400 flex flex-col items-center justify-center p-8 relative overflow-hidden">
        {/* Decorative arcs */}
        <div className="absolute top-[-20%] right-[-10%] w-[500px] h-[500px] rounded-full border border-white/10" />
        <div className="absolute bottom-[-30%] left-[-15%] w-[600px] h-[600px] rounded-full border border-white/10" />
        <div className="absolute top-[10%] left-[5%] w-[200px] h-[200px] rounded-full border border-white/5" />

        <motion.div {...animations.fadeUp} className="text-center space-y-5 max-w-4xl relative z-10">
          {label && <SectionLabel variant="blue">{label}</SectionLabel>}
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white">
            {title}
            {titleAccent && <span className="text-blue-200"> {titleAccent}</span>}
          </h1>
          {subtitle && (
            <p className="text-xl md:text-2xl text-blue-100 max-w-2xl mx-auto font-light">
              {subtitle}
            </p>
          )}
        </motion.div>
      </div>
    );
  }

  // Light variant (default)
  return (
    <div className="h-full w-full bg-[#F8FAFC] flex flex-col items-center justify-center p-8 relative overflow-hidden">
      {/* Subtle dot grid */}
      <div className={`absolute inset-0 ${tw.subtleDotGrid} opacity-40`} />
      {/* Decorative blue arcs */}
      <div className="absolute top-[-15%] right-[-8%] w-[400px] h-[400px] rounded-full border-2 border-blue-100/40" />
      <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full border-2 border-blue-100/30" />

      <motion.div {...animations.fadeUp} className="text-center space-y-5 max-w-4xl relative z-10">
        {icon && (
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 mb-2 border border-blue-100">
            {icon}
          </div>
        )}
        {label && <SectionLabel>{label}</SectionLabel>}
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-slate-900">
          {title}
          {titleAccent && <span className="text-blue-600"> {titleAccent}</span>}
        </h1>
        {subtitle && (
          <p className="text-xl md:text-2xl text-slate-400 max-w-2xl mx-auto font-light">
            {subtitle}
          </p>
        )}
      </motion.div>
    </div>
  );
};

// ─── Slide Wrapper ───────────────────────────────────────────────
// Consistent wrapper for slide content. Provides background, padding,
// and optional decorative elements.
export const SlideWrapper = ({
  children,
  variant = 'light', // 'light' | 'dark' | 'blue' | 'white'
  className = '',
  withDots = false,
  withArcs = false,
}) => {
  const bgClass = {
    light: 'bg-[#F8FAFC]',
    dark: 'bg-[#0F172A]',
    blue: 'bg-gradient-to-br from-blue-600 via-blue-500 to-blue-400',
    white: 'bg-white',
  }[variant];

  return (
    <div className={`h-full w-full ${bgClass} relative overflow-hidden ${className}`}>
      {withDots && (
        <div
          className={`absolute inset-0 ${tw.subtleDotGrid} ${
            variant === 'dark' || variant === 'blue' ? 'opacity-10' : 'opacity-30'
          }`}
        />
      )}
      {withArcs && (
        <>
          <div
            className={`absolute top-[-15%] right-[-8%] w-[400px] h-[400px] rounded-full border-2 ${
              variant === 'dark' || variant === 'blue'
                ? 'border-white/5'
                : 'border-blue-100/30'
            }`}
          />
          <div
            className={`absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full border-2 ${
              variant === 'dark' || variant === 'blue'
                ? 'border-white/5'
                : 'border-blue-100/20'
            }`}
          />
        </>
      )}
      <div className="relative z-10 h-full">{children}</div>
    </div>
  );
};

// ─── Feature Card ────────────────────────────────────────────────
// Clean card for feature grids. Matches BRIX card style:
// white background, soft shadow, rounded corners.
export const FeatureCard = ({
  icon,
  title,
  description,
  className = '',
  index = 0,
}) => (
  <motion.div
    {...animations.stagger(index)}
    className={`${tw.cardHover} p-6 ${className}`}
  >
    {icon && (
      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 mb-4 border border-blue-100">
        {icon}
      </div>
    )}
    {title && <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>}
    {description && <p className="text-sm text-slate-500 leading-relaxed">{description}</p>}
  </motion.div>
);

// ─── Stat Badge ──────────────────────────────────────────────────
// For displaying key statistics/metrics.
export const StatBadge = ({
  value,
  label,
  variant = 'light',
  className = '',
}) => (
  <div
    className={`text-center px-6 py-4 rounded-xl ${
      variant === 'dark'
        ? 'bg-white/5 border border-white/10'
        : 'bg-white border border-slate-200/60 shadow-sm'
    } ${className}`}
  >
    <div
      className={`text-2xl font-bold ${
        variant === 'dark' ? 'text-white' : 'text-slate-900'
      }`}
    >
      {value}
    </div>
    <div
      className={`text-xs mt-1 ${
        variant === 'dark' ? 'text-slate-400' : 'text-slate-500'
      }`}
    >
      {label}
    </div>
  </div>
);

// ─── Decorative Dot Pattern ──────────────────────────────────────
// Blue dot grid pattern (like the BRIX AI logo motif).
export const DotPattern = ({ className = '' }) => (
  <div className={`grid grid-cols-3 gap-1.5 ${className}`}>
    {[...Array(9)].map((_, i) => (
      <div key={i} className="w-2 h-2 rounded-full bg-blue-600" />
    ))}
  </div>
);
